/**
 * O formulário público e anônimo do Canal de Denúncia (Lei 14.457/2022).
 *
 * Aberto pelo link da empresa (`/canal-de-denuncia/:token`) — quem denuncia não tem conta e não
 * pode cair no login. Ao enviar, recebe o protocolo para acompanhar sem se identificar.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';

import denunciaService from '../../services/denunciaService';
import {
  CATEGORIAS_DE_DENUNCIA,
  ROTULO_CATEGORIA,
  DadosDaDenunciaPublica,
  DenunciaCategoria,
} from '../../types/denuncia';

function formularioVazio(): DadosDaDenunciaPublica {
  return {
    categoria: '',
    titulo: '',
    descricao: '',
    local: '',
    dataDoOcorrido: '',
    envolvidos: '',
    anonima: true,
    relatorNome: '',
    relatorEmail: '',
    relatorTelefone: '',
  };
}

export const DenunciaPublicaPage: React.FC = () => {
  const { token = '' } = useParams();
  const [empresa, setEmpresa] = useState<string>('');
  const [canalValido, setCanalValido] = useState<boolean | null>(null);
  const [dados, setDados] = useState<DadosDaDenunciaPublica>(formularioVazio);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [protocolo, setProtocolo] = useState<string | null>(null);
  const [anexos, setAnexos] = useState<string[]>([]);
  const [anexando, setAnexando] = useState(false);

  const anexarProva = useCallback(async (arquivo: File | undefined) => {
    if (!arquivo || !protocolo) return;
    setAnexando(true);
    setErro(null);
    try {
      await denunciaService.anexarPorProtocolo(protocolo, arquivo);
      setAnexos((atuais) => [...atuais, arquivo.name]);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível anexar o arquivo.');
    } finally {
      setAnexando(false);
    }
  }, [protocolo]);

  useEffect(() => {
    denunciaService
      .abrirCanal(token)
      .then((r) => {
        setEmpresa(r.empresa);
        setCanalValido(true);
      })
      .catch(() => setCanalValido(false));
  }, [token]);

  const enviar = useCallback(async () => {
    setErro(null);
    if (!dados.categoria || !dados.titulo.trim() || !dados.descricao.trim()) {
      setErro('Informe a categoria, um título e a descrição do ocorrido.');
      return;
    }
    setEnviando(true);
    try {
      const { protocolo: p } = await denunciaService.enviarDenuncia(token, dados);
      setProtocolo(p);
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível enviar a denúncia.');
    } finally {
      setEnviando(false);
    }
  }, [dados, token]);

  if (canalValido === false) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', p: 3 }}>
        <Alert severity="error">Este canal de denúncia não está disponível.</Alert>
      </Box>
    );
  }

  if (protocolo) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', p: 3 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>Denúncia registrada</Typography>
          <Typography sx={{ mb: 2 }}>
            Guarde o protocolo abaixo. É com ele que você acompanha a apuração, sem precisar se
            identificar.
          </Typography>
          <Typography variant="h4" sx={{ fontFamily: 'monospace', my: 2 }}>{protocolo}</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Anote este protocolo agora — ele não é reenviado e não pode ser recuperado.
          </Alert>
          <Typography sx={{ mb: 3 }}>
            Acompanhe o andamento a qualquer momento em{' '}
            <a href={`${window.location.origin}/acompanhar-denuncia`} target="_blank" rel="noreferrer">
              {window.location.host}/acompanhar-denuncia
            </a>
            , usando este protocolo.
          </Typography>

          <Typography variant="subtitle2" gutterBottom>Anexar provas (opcional)</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Documentos, fotos ou prints que ajudem a apurar. PDF ou imagem, até 10 MB.
          </Typography>
          <Button variant="outlined" component="label" disabled={anexando}>
            {anexando ? 'Enviando…' : 'Escolher arquivo'}
            <input hidden type="file" accept=".pdf,image/*"
              onChange={(e) => void anexarProva(e.target.files?.[0])} />
          </Button>
          {anexos.length > 0 && (
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              {anexos.map((nome, i) => (
                <Typography key={i} variant="body2" color="success.main">✓ {nome}</Typography>
              ))}
            </Stack>
          )}
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: 3 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom>Canal de Denúncia</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {empresa} — sua denúncia é sigilosa. Você pode enviá-la de forma anônima.
        </Typography>

        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        <Stack spacing={2}>
          <FormControl fullWidth required>
            <InputLabel id="cat">Categoria</InputLabel>
            <Select
              labelId="cat"
              label="Categoria"
              value={dados.categoria}
              onChange={(e) => setDados({ ...dados, categoria: e.target.value as DenunciaCategoria })}
            >
              {CATEGORIAS_DE_DENUNCIA.map((c) => (
                <MenuItem key={c} value={c}>{ROTULO_CATEGORIA[c]}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField label="Título" required value={dados.titulo}
            onChange={(e) => setDados({ ...dados, titulo: e.target.value })} fullWidth />

          <TextField label="Descreva o ocorrido" required multiline minRows={4}
            value={dados.descricao} onChange={(e) => setDados({ ...dados, descricao: e.target.value })} fullWidth />

          <TextField label="Local (opcional)" value={dados.local}
            onChange={(e) => setDados({ ...dados, local: e.target.value })} fullWidth />

          <TextField label="Data do ocorrido (opcional)" type="date" value={dados.dataDoOcorrido}
            onChange={(e) => setDados({ ...dados, dataDoOcorrido: e.target.value })}
            fullWidth InputLabelProps={{ shrink: true }} />

          <TextField label="Pessoas envolvidas / testemunhas (opcional)" multiline minRows={2}
            value={dados.envolvidos} onChange={(e) => setDados({ ...dados, envolvidos: e.target.value })} fullWidth />

          <FormControlLabel
            control={
              <Checkbox checked={dados.anonima}
                onChange={(e) => setDados({ ...dados, anonima: e.target.checked })} />
            }
            label="Quero permanecer anônimo"
          />

          {!dados.anonima && (
            <Stack spacing={2}>
              <TextField label="Seu nome" value={dados.relatorNome}
                onChange={(e) => setDados({ ...dados, relatorNome: e.target.value })} fullWidth />
              <TextField label="E-mail" value={dados.relatorEmail}
                onChange={(e) => setDados({ ...dados, relatorEmail: e.target.value })} fullWidth />
              <TextField label="Telefone" value={dados.relatorTelefone}
                onChange={(e) => setDados({ ...dados, relatorTelefone: e.target.value })} fullWidth />
            </Stack>
          )}

          <Button variant="contained" size="large" onClick={() => void enviar()} disabled={enviando}>
            {enviando ? 'Enviando…' : 'Enviar denúncia'}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
};

export default DenunciaPublicaPage;
